import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { PenSquare, Plus } from 'lucide-react';

// ProfilePage Component
export default function ProfilePage() {
  const [name, setName] = useState("John Doe");
  const [email, setEmail] = useState("john.doe@example.com");
  const [bio, setBio] = useState("Passionate storyteller and life explorer");
  const [activeTab, setActiveTab] = useState("canvas");
  const [publishedStories, setPublishedStories] = useState([]);
  const [publishedCanvases, setPublishedCanvases] = useState([]);

  // Fetch published stories and canvases from local storage
  useEffect(() => {
    const stories = JSON.parse(localStorage.getItem('publishedStories') || '[]');
    const canvases = stories.filter(story => story.type === 'lifestory');
    const anecdotes = stories.filter(story => story.type === 'anecdote');

    setPublishedStories(anecdotes);
    setPublishedCanvases(canvases);
  }, []);

  const handleEdit = (topic, type, content) => {
    const url = `/canvas?topic=${encodeURIComponent(topic)}&type=${type}&content=${encodeURIComponent(content)}`;
    window.location.href = url; // Redirect to CanvasPage with query parameters
  };

  const ContentCard = ({ title, items, addNewText }) => (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-xl font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[200px] w-full">
          {items.map((item, index) => (
            <div key={index} className="mb-4 p-2 border rounded-md">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">{item.topic || item.title}</h3>
                <Button variant="ghost" size="sm" onClick={() => handleEdit(item.topic, item.type, item.content)}>
                  <PenSquare className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-600 mt-1">{item.content || item.excerpt}</p>
            </div>
          ))}
        </ScrollArea>
        <Button className="mt-4 w-full" variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          {addNewText}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 bg-gray-50">
      <Card className="max-w-4xl mx-auto shadow-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Your Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4 mb-6">
            <Avatar className="w-20 h-20">
              <AvatarImage src="/placeholder.svg?height=80&width=80" alt="Profile picture" />
              <AvatarFallback>JD</AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-semibold">{name}</h2>
              <p className="text-gray-500 text-sm">Joined: January 2023</p>
            </div>
          </div>
          <div className="space-y-4 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700">Bio</label>
              <Input id="bio" value={bio} onChange={(e) => setBio(e.target.value)} className="mt-1" />
            </div>
            <Button className="w-full bg-black text-white hover:bg-gray-800">Update Profile</Button>
          </div>
         
          <div className="w-full">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-md">
              {["canvas", "stories", "journal"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-2 text-center rounded-md transition-colors ${
                    activeTab === tab ? "bg-white shadow-sm" : "hover:bg-gray-200"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
            <div className="mt-4">
              {activeTab === "canvas" && (
                <ContentCard
                  title="Your Life Canvas Entries"
                  items={publishedCanvases}
                  addNewText="Add New Canvas Entry"
                />
              )}
              {activeTab === "stories" && (
                <ContentCard
                  title="Your Stories"
                  items={publishedStories}
                  addNewText="Write a New Story"
                />
              )}
              {activeTab === "journal" && (
                <ContentCard
                  title="Your Journal Entries"
                  items={mockUserContent.journal}
                  addNewText="Add New Journal Entry"
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}